import dayjs from "dayjs";
import { MapDeliveryJobStatus } from "app/types/mapScreen";
import { formatUtcScheduledDate } from "app/utils/timezone";

type MapPinTooltipJob = {
  pickingNumber?: string;
  partnerName?: string;
  address?: string;
  name?: string;
  customerName?: string;
  scheduledDateLabel?: string;
  status?: MapDeliveryJobStatus;
  routeName?: string;
};

const STATUS_META: Record<
  MapDeliveryJobStatus,
  { label: string; color: string; bg: string }
> = {
  unassigned: { label: "Unassigned", color: "#b45309", bg: "#fef3c7" },
  pending: { label: "Assigned", color: "#1d4ed8", bg: "#dbeafe" },
  in_route: { label: "In route", color: "#6d28d9", bg: "#ede9fe" },
  delivered: { label: "Delivered", color: "#15803d", bg: "#dcfce7" },
  failed: { label: "Failed", color: "#b91c1c", bg: "#fee2e2" },
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function formatMapPinScheduledDate(
  value: string | undefined,
  timezone: string
): string | undefined {
  if (!value) {
    return undefined;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("MM/DD/YYYY") : value;
  }

  const formatted = formatUtcScheduledDate(value, timezone, "MM/DD/YYYY h:mm A");
  return formatted || undefined;
}

function renderStatusBadge(status?: MapDeliveryJobStatus): string {
  if (!status) {
    return "";
  }
  const meta = STATUS_META[status] || STATUS_META.pending;
  return `<span style="display:inline-block;margin-top:3px;padding:1px 6px;border-radius:999px;font-size:10px;font-weight:600;line-height:1.4;color:${meta.color};background:${meta.bg};">${escapeHtml(meta.label)}</span>`;
}

function countUnassignedJobs(
  jobs: Array<{ status?: MapDeliveryJobStatus }>
): number {
  return jobs.filter((job) => job.status === "unassigned").length;
}

export function buildMapPinTooltipHtml(job: MapPinTooltipJob): string {
  const pickingNumber = escapeHtml(
    job.pickingNumber || job.name || "—"
  );
  const partnerName = escapeHtml(job.partnerName || job.customerName || "—");
  const address = escapeHtml(job.address || "—");
  const scheduledDate = job.scheduledDateLabel
    ? `<div style="color:#64748b;margin-bottom:2px;">${escapeHtml(job.scheduledDateLabel)}</div>`
    : "";
  const statusBadge = renderStatusBadge(job.status);
  const routeLine =
    job.routeName && job.status !== "unassigned"
      ? `<div style="color:#64748b;font-size:11px;margin-bottom:2px;">Route: ${escapeHtml(job.routeName)}</div>`
      : "";

  return `
    <div class="route-map-pin-tooltip-content" style="font-size:12px;line-height:1.45;max-width:240px;">
      <div style="font-weight:700;color:#0f172a;margin-bottom:4px;">${pickingNumber}</div>
      <div style="color:#334155;margin-bottom:2px;">${partnerName}</div>
      ${routeLine}
      ${scheduledDate}
      ${statusBadge}
      <div style="color:#64748b;margin-top:4px;">${address}</div>
    </div>
  `;
}

type MapPinTooltipGroupJob = MapPinTooltipJob & {
  id?: string;
  scheduledDate?: string;
};

export function buildMapPinGroupTooltipHtml(
  jobs: MapPinTooltipGroupJob[],
  timezone: string
): string {
  if (jobs.length === 1) {
    const job = jobs[0];
    return buildMapPinTooltipHtml({
      ...job,
      scheduledDateLabel:
        job.scheduledDateLabel ||
        formatMapPinScheduledDate(job.scheduledDate, timezone),
    });
  }

  const unassignedCount = countUnassignedJobs(jobs);
  const assignedCount = jobs.length - unassignedCount;
  const summaryParts = [`${jobs.length} orders at this stop`];
  if (unassignedCount > 0) {
    summaryParts.push(`${unassignedCount} unassigned`);
  }
  if (assignedCount > 0) {
    summaryParts.push(`${assignedCount} assigned`);
  }

  const header = `<div style="font-weight:700;color:#0f172a;margin-bottom:6px;font-size:12px;line-height:1.35;">${escapeHtml(summaryParts.join(" · "))}</div>`;
  const rows = jobs
    .map((job) => {
      const pickingNumber = escapeHtml(job.pickingNumber || job.name || "—");
      const partnerName = escapeHtml(job.partnerName || job.customerName || "—");
      const scheduledDate = formatMapPinScheduledDate(job.scheduledDate, timezone);
      const dateLine = scheduledDate
        ? `<div style="color:#94a3b8;font-size:11px;">${escapeHtml(scheduledDate)}</div>`
        : "";
      const routeLine =
        job.routeName && job.status !== "unassigned"
          ? `<div style="color:#94a3b8;font-size:11px;">Route: ${escapeHtml(job.routeName)}</div>`
          : "";
      return `
        <div style="padding:6px 0;border-bottom:1px solid #eef1f4;">
          <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:8px;">
            <div style="font-weight:600;color:#334155;font-size:11px;">${pickingNumber}</div>
            ${renderStatusBadge(job.status)}
          </div>
          <div style="color:#64748b;font-size:11px;margin-top:2px;">${partnerName}</div>
          ${routeLine}
          ${dateLine}
        </div>
      `;
    })
    .join("");

  return `
    <div class="route-map-pin-tooltip-content route-map-pin-tooltip-scroll" style="font-size:12px;line-height:1.45;max-width:280px;max-height:220px;overflow-y:auto;pointer-events:auto;">
      ${header}
      ${rows}
    </div>
  `;
}

export const MAP_PIN_TOOLTIP_OPTIONS = {
  direction: "top" as const,
  offset: [0, -42] as [number, number],
  opacity: 0.96,
  className: "route-map-pin-tooltip",
  interactive: true,
  sticky: false,
};
